import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { Observable, Observer } from 'rxjs';

import { EVENT_SOURCE_URL } from './tokens.service';

export interface UploadProgressEvent {
    /** 0–100; -1 signals an error */
    progress: number;
    phase: 'checking' | 'saving' | 'reading' | 'caching' | 'creating' | 'done' | 'error';
    message: string;
    sheetsProcessed?: number;
    totalSheets?: number;
    /** Set on the final done event */
    batchId?: number;
    /** True when an already-completed batch was reused */
    isExisting?: boolean;
    /** Structured error code on phase === 'error' */
    code?: string;
}

export interface WorkbookUploadOptions {
    importer: 'premium-data' | 'ageing' | 'annexure' | 'performance' | 'claim-data' | 'sales';
    file: File;
    fields: Record<string, string | number | null | undefined>;
}

/**
 * Uploads a workbook to the server and polls for caching progress.
 *
 * Flow:
 *   1. POST /workbook/upload-start  → returns { uploadId }
 *   2. GET  /workbook/upload-progress/:uploadId  (polled every second)
 *   3. When progress === 100 → complete;  progress === -1 → error
 */
@Injectable({ providedIn: 'root' })
export class WorkbookUploadService {
    private oktaAuth = inject(OKTA_AUTH);
    private http     = inject(HttpClient);
    private baseUrl  = inject(EVENT_SOURCE_URL);

    upload(options: WorkbookUploadOptions): Observable<UploadProgressEvent> {
        return new Observable((observer: Observer<UploadProgressEvent>) => {
            const accessToken = this.oktaAuth.getAccessToken();
            const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}` });

            // Build multipart form data
            const formData = new FormData();
            formData.append('file', options.file, options.file.name);
            formData.append('importer', options.importer);

            for (const [key, value] of Object.entries(options.fields)) {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            }

            let pollTimer: ReturnType<typeof setInterval> | null = null;
            let completed = false;

            // Step 1: POST the file — server returns { uploadId } immediately
            this.http
                .post<{ uploadId: string }>(`${this.baseUrl}/workbook/upload-start`, formData, { headers })
                .subscribe({
                    next: ({ uploadId }) => {
                        // Emit an initial event so the UI shows something right away
                        observer.next({ progress: 1, phase: 'checking', message: 'Starting upload...' });

                        // Step 2: Poll for progress every second
                        pollTimer = setInterval(() => {
                            if (completed) return;

                            this.http
                                .get<UploadProgressEvent>(
                                    `${this.baseUrl}/workbook/upload-progress/${uploadId}`,
                                    { headers }
                                )
                                .subscribe({
                                    next: (event) => {
                                        if (completed) return;

                                        observer.next(event);

                                        if (event.progress === 100) {
                                            completed = true;
                                            if (pollTimer) clearInterval(pollTimer);
                                            observer.complete();
                                        }
                                        else if (event.progress === -1) {
                                            completed = true;
                                            if (pollTimer) clearInterval(pollTimer);
                                            const err: any = new Error(event.message || 'Upload failed');
                                            err.code = event.code;
                                            observer.error(err);
                                        }
                                    },
                                    error: () => {
                                        // Transient poll failure — keep trying
                                    }
                                });
                        }, 1000);
                    },
                    error: (err) => {
                        const msg = err?.error?.error || err?.message || 'Upload failed';
                        observer.error(new Error(msg));
                    }
                });

            // Cleanup when the subscriber unsubscribes
            return () => {
                completed = true;
                if (pollTimer) clearInterval(pollTimer);
            };
        });
    }
}
